import React, { useEffect, useRef, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import Header from "./components/Header";
import { ThemeProvider } from "@mui/material";
import theme from "./theme";
import ScrollToTop from "./components/ScrollToTop";
import Bookings from "./pages/Bookings";
const ChatWidget = React.lazy(() => import("./components/chatWidget"));

// ── Lazy-loaded page components ───────────────────────────────────────────────
const HomePage = React.lazy(() => import("./pages/HomePage"));
const HomePageCopy = React.lazy(() => import("./pages/HomePageCopy"));
const Careers = React.lazy(() => import("./pages/Careers"));
const JoinOurTeam = React.lazy(() => import("./inner-page/JoinOurTeam"));
const OurWork = React.lazy(() => import("./pages/Work"));
const CaseStudy = React.lazy(() => import("./pages/CaseStudy"));
const ThankYouPage = React.lazy(() => import("./pages/ThankYouPage"));
const ContactUs = React.lazy(() => import("./pages/ContactUs"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const NotFoundPage = React.lazy(() => import("./pages/NotFoundPage"));
const ServicePages = React.lazy(() => import("./pages/ServicePages"));
const ControlPanel = React.lazy(() => import("./pages/ControlPanel"));
const SharePointTrainings = React.lazy(
  () => import("./inner-page/SharePointTrainings"),
);
const ArchiveSharePointTrainings = React.lazy(
  () => import("./inner-page/ArchiveSharePointTrainings"),
);
const SharePointEvents = React.lazy(() => import("./pages/SharePointEvents"));
const ArchiveSharePointEvents = React.lazy(
  () => import("./inner-page/ArchiveSharePointEvents"),
);

// ── Lazy-loaded case study sub-section components ─────────────────────────────
const SharepointConsultingMicrosoft1 = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/sharepoint-consulting-wa-usa-microsoft-1"),
);
const SharepointConsultingPega1 = React.lazy(
  () => import("./sub-sections/CaseStudy/sharepoint-consulting-ma-usa-pega-1"),
);
const MachineLearningMetriq1 = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/machine-learning-ai-&-react-france-metriq-1"),
);
const ApplicatioDevelopmentPinscriptive1 = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/application-development-ca-usa-pinscriptive-1"),
);
const MobileAppFibronostics1 = React.lazy(
  () => import("./sub-sections/CaseStudy/mobile-app-singapore-fibronostics-1"),
);
const SharepointConsultingPega2 = React.lazy(
  () => import("./sub-sections/CaseStudy/sharepoint-consulting-ma-usa-pega-2"),
);
const SharepointConsultingBlythedale1 = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/sharepoint-consulting-ny-usa-blythedale-1"),
);
const SharepointConsultingBlythedale2 = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/sharepoint-consulting-ny-usa-blythedale-2"),
);
const SharepointConsultingGlobalSubmit1 = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/sharepoint-consulting-pa-usa-globalsubmit-1"),
);
const SharepointConsultingUSAmeribank1 = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/sharepoint-consulting-fl-usa-usameribank-1"),
);
const SharepointConsultingProgressiveFinancial1 = React.lazy(
  () => import("./sub-sections/CaseStudy/sharepoint-consulting-az-usa-pfs-1"),
);
const SharepointConsultingCDMSmith1 = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/sharepoint-consulting-massachusetts-usa-cdm-smith-1"),
);
const SharepointConsultingZSPharma1 = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/sharepoint-consulting-ca-usa-zspharma-1"),
);
const SharepointConsultingAdvancedRadiology1 = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/sharepoint-consulting-ct-usa-advanced-radiology-1"),
);
const SharepointConsultingWRHI1 = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/sharepoint-consulting-Johannesburg-southafrica-wrhi-1"),
);
const SharepointConsultingFlohut1 = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/sharepoint-consulting-fl-usa-flohut-1"),
);
const SharepointConsultingPace1 = React.lazy(
  () => import("./sub-sections/CaseStudy/sharepoint-consulting-fl-usa-pace-1"),
);
const SharepointConsultingSJHC1 = React.lazy(
  () => import("./sub-sections/CaseStudy/sharepoint-consulting-nj-usa-sjhc-1"),
);
const SharepointConsultingMicrosoft2 = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/sharepoint-consulting-wa-usa-microsoft-2"),
);
const SharepointConsultingMicrosoft3 = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/sharepoint-consulting-usa-microsoft-3"),
);
const MicrosoftCopilotAgents = React.lazy(
  () => import("./sub-sections/CaseStudy/sharepoint-ai-agent-usa-six-1"),
);
const SharepointConsultingNorwayNorconsult = React.lazy(
  () => import("./sub-sections/CaseStudy/sharepoint-norway-norconsult-1"),
);
const PowerAppNorwayNorconsult = React.lazy(
  () => import("./sub-sections/CaseStudy/power-app-norway-norconsult-1"),
);
const NotalScanlyHomeOct = React.lazy(
  () => import("./sub-sections/CaseStudy/web-app-usa-notalvision-1"),
);
const PolygonPowerapps = React.lazy(
  () => import("./sub-sections/CaseStudy/power-automate-norway-polygon-1"),
);
const CSODMindMap = React.lazy(
  () => import("./sub-sections/CaseStudy/ai-mindmap-generator-us-csod-1"),
);
const CSODMailAssistant = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/generative-ai-mail-assistant-us-csod-4"),
);
const CSODPPTAssistant = React.lazy(
  () => import("./sub-sections/CaseStudy/ai-ppt-assistant-us-csod-2"),
);
const CSODQueryTool = React.lazy(
  () => import("./sub-sections/CaseStudy/ai-query-tool-us-csod-3"),
);
const CSODGrammarAssistant = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/ai-writing-intelligence-platform-us-csod-6"),
);
const CSODContenAsistant = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/ai-content-creation-automation-us-csod-7"),
);
const CSODSheetInsightsAI = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/ai-spreadsheet-insights-platform-us-csod-8"),
);
const CSODQuoteFlowAI = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/ai-quote-automation-platform-us-csod-9"),
);
const CSODCornerCast = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/ai-knowledge-casting-platform-us-csod-5"),
);
const IntelligentPayment = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/payment-integration-automation-uk-accurise-1"),
);
const IntelligentInvoice = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/intelligent-invoice-processing-automation-uk-accurise-2"),
);
const GenerativeAI = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/generative-ai-lifestyle-image-platform-us-komar-1"),
);
const EDIIntegration = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/enterprise-edi-integration-warehouse-automation-us-komar-2"),
);
const EnterpriseCloudCostOptimizationAWS = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/enterprise-cloud-cost-optimization-aws-1"),
);
const EmployeeOnboardingAutomation = React.lazy(
  () =>
    import("./sub-sections/CaseStudy/employee-onboarding-automation-india-binary-republik-1"),
);

// ── Lazy-loaded business solution sub-section components ──────────────────────
const AutomateInvoiceProcessing = React.lazy(
  () => import("./sub-sections/BusinessSolution/AutomateInvoiceProcessing"),
);
const AutomatePaymentProcessing = React.lazy(
  () => import("./sub-sections/BusinessSolution/AutomatePaymentProcessing"),
);
const UnlockingSalesforceCPQ = React.lazy(
  () => import("./sub-sections/BusinessSolution/UnlockingSalesforceCPQ"),
);

const isAuthenticated = () => {
  const token = localStorage.getItem("token");
  const expiryTime = localStorage.getItem("expiryTime");
  if (!token || !expiryTime) return false;
  if (Date.now() > parseInt(expiryTime)) {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("expiryTime");
    window.location.href = "/controlpanel/login";
    return false;
  }
  return true;
};

const hasRole = (allowedRoles: string[]) => {
  const userRole = localStorage.getItem("userRole");
  return userRole && allowedRoles.includes(userRole);
};

const ProtectedRoute = ({
  element,
  allowedRoles,
}: {
  element: JSX.Element;
  allowedRoles: string[];
}) => {
  if (!isAuthenticated()) return <Navigate to="/controlpanel/login" replace />;
  if (!hasRole(allowedRoles))
    return <Navigate to="/controlpanel/login" replace />;
  return element;
};

const App: React.FC = () => {
  const location = useLocation();
  const topRef = useRef<HTMLDivElement>(null);

  // ── EXISTING: scroll to top on route change ────────────────────────────────
  useEffect(() => {
    if (
      !location.hash &&
      location.hash != "#services" &&
      location.hash != "#process" &&
      location.hash != "#businesssolution"
    ) {
      if (!location.hash) {
        const timer = setTimeout(() => {
          if (topRef.current) {
            topRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [location.pathname]);

  // ── ADDED: capture UTM params on first page load ───────────────────────────
  // Runs once per session. Stores source, medium, campaign, keyword in
  // sessionStorage so LetsTalk.tsx can read them when the form is submitted —
  // even if the user landed on the homepage first and navigated to /contact later.
  useEffect(() => {
    if (sessionStorage.getItem("br_tracking_data")) return; // already captured this session

    const params = new URLSearchParams(window.location.search);
    let source = params.get("utm_source") || "";
    let medium = params.get("utm_medium") || "";
    const campaign = params.get("utm_campaign") || "";
    const term = params.get("utm_term") || "";
    const referrer = document.referrer || "";

    // Derive source from referrer when no UTM params are present (organic/referral traffic)
    if (!source && referrer) {
      try {
        const host = new URL(referrer).hostname.replace("www.", "");
        if (host.includes("google")) {
          source = "google";
          medium = medium || "organic";
        } else if (host.includes("bing")) {
          source = "bing";
          medium = medium || "organic";
        } else if (host.includes("yahoo")) {
          source = "yahoo";
          medium = medium || "organic";
        } else if (host.includes("facebook")) {
          source = "facebook";
          medium = medium || "social";
        } else if (host.includes("linkedin")) {
          source = "linkedin";
          medium = medium || "social";
        } else if (host.includes("twitter") || host.includes("x.com")) {
          source = "twitter";
          medium = medium || "social";
        } else {
          source = host;
          medium = medium || "referral";
        }
      } catch (_) {
        /* malformed referrer — ignore */
      }
    }

    // If still no source — user typed URL directly or came from a bookmark
    if (!source) {
      source = "direct";
      medium = "none";
    }

    sessionStorage.setItem(
      "br_tracking_data",
      JSON.stringify({
        utm_source: source,
        utm_medium: medium,
        utm_campaign: campaign,
        utm_term: term,
        landing_page: window.location.pathname,
        referrer_url: referrer,
        captured_at: new Date().toISOString(),
      }),
    );
  }, [location.pathname]);
  // ── END ADDED ──────────────────────────────────────────────────────────────

  return (
    <div ref={topRef} id="container-main-id">
      <ThemeProvider theme={theme}>
        <div className="containerfluid">
          <Header />
          <ScrollToTop />
          <main>
            <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
              <Routes>
                <Route path="*" element={<NotFoundPage />} />
                <Route path="/" element={<HomePage />} />
                <Route path="/home" element={<HomePageCopy />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/work" element={<OurWork />} />
                <Route path="/thankyou" element={<ThankYouPage />} />
                <Route path="/case-study/:name" element={<CaseStudy />} />
                <Route path="/careers/:id" element={<JoinOurTeam />} />
                <Route path="/bookings" element={<Bookings />} />
                <Route path="/artificial-intelligence" element={<ServicePages pageName='ArtificialIntelligence' />} />
                <Route path="/enterprise-ai-solutions" element={<ServicePages pageName='EnterpriseAISolutionsAndStrategicConsulting' />} />
                <Route path="/data-engineering-solutions" element={<ServicePages pageName='DataEngineeringSolutions' />} />
                <Route path="/devops-cloud-engineering" element={<ServicePages pageName='DevOpsAndCloudEngineeringServices' />} />
                <Route path="/sharepoint-consulting" element={<ServicePages pageName='SharepointConsulting' />} />
                <Route path="/salesforce-consulting" element={<ServicePages pageName='SalesforceConsulting' />} />
                <Route path="/azure-aws-cloud" element={<ServicePages pageName='AzureAWSCloud' />} />
                <Route path="/microsoft-power-platform" element={<ServicePages pageName='MicrosoftPowerPlatform' />} />
                <Route path="/mobile-solutions" element={<ServicePages pageName='MobileSolutions' />} />
                <Route path="/custom-application-saas" element={<ServicePages pageName='CustomApplicationDevelopment' />} />
                <Route path="/content-management-systems" element={<ServicePages pageName='ContentManagementSystems' />} />
                <Route path="/microsoft-copilot" element={<ServicePages pageName='MsCopilotSection' />} />
                <Route path="/identity-access-management" element={<ServicePages pageName='IAMSection' />} />
                <Route path="/uiux-design" element={<ServicePages pageName='UIUXDesign' />} />
                <Route path="/electronic-data-interchange" element={<ServicePages pageName='ElectronicDataInterchange' />} />
                <Route path="/modernize-your-legacy-system" element={<ServicePages pageName='ModernizeYourLegacySystem' />} />

                <Route path="/contact" element={<ContactUs />} />
                <Route
                  path="/sharepoint-m365-ai-trainings"
                  element={<SharePointTrainings />}
                />
                <Route
                  path="/sharepoint-m365-ai-trainings-archive"
                  element={<ArchiveSharePointTrainings />}
                />
                <Route
                  path="/sharepoint-m365-azure-ai-events"
                  element={<SharePointEvents />}
                />
                <Route
                  path="/sharepoint-m365-azure-ai-events-archive"
                  element={<ArchiveSharePointEvents />}
                />
                <Route
                  path="/controlpanel/contactvisitors"
                  element={
                    <ProtectedRoute
                      element={<ControlPanel tabName="ContactRequest" />}
                      allowedRoles={["Admin", "HR"]}
                    />
                  }
                />
                <Route
                  path="/controlpanel/careervisitors"
                  element={
                    <ProtectedRoute
                      element={<ControlPanel tabName="JobRequest" />}
                      allowedRoles={["Admin", "HR"]}
                    />
                  }
                />
                <Route
                  path="/controlpanel/showcasevisitors"
                  element={
                    <ProtectedRoute
                      element={<ControlPanel tabName="ShowcaseRequest" />}
                      allowedRoles={["Admin", "HR"]}
                    />
                  }
                />
                <Route
                  path="/controlpanel/login"
                  element={<ControlPanel tabName="Login" />}
                />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route
                  path="/case-study/sharepoint-consulting-ma-usa-pega-1"
                  element={<SharepointConsultingPega1 />}
                />
                <Route
                  path="/case-study/sharepoint-consulting-ma-usa-pega-2"
                  element={<SharepointConsultingPega2 />}
                />
                <Route
                  path="/case-study/sharepoint-consulting-ny-usa-blythedale-1"
                  element={<SharepointConsultingBlythedale1 />}
                />
                <Route
                  path="/case-study/sharepoint-consulting-ny-usa-blythedale-2"
                  element={<SharepointConsultingBlythedale2 />}
                />
                <Route
                  path="/case-study/sharepoint-consulting-pa-usa-globalsubmit-1"
                  element={<SharepointConsultingGlobalSubmit1 />}
                />
                <Route
                  path="/case-study/sharepoint-consulting-fl-usa-usameribank-1"
                  element={<SharepointConsultingUSAmeribank1 />}
                />
                <Route
                  path="/case-study/sharepoint-consulting-nj-usa-sjhc-1"
                  element={<SharepointConsultingSJHC1 />}
                />
                <Route
                  path="/case-study/sharepoint-consulting-wa-usa-microsoft-1"
                  element={<SharepointConsultingMicrosoft1 />}
                />
                <Route
                  path="/case-study/sharepoint-consulting-ca-usa-zspharma-1"
                  element={<SharepointConsultingZSPharma1 />}
                />
                <Route
                  path="/case-study/sharepoint-consulting-az-usa-pfs-1"
                  element={<SharepointConsultingProgressiveFinancial1 />}
                />
                <Route
                  path="/case-study/sharepoint-consulting-Johannesburg-southafrica-wrhi-1"
                  element={<SharepointConsultingWRHI1 />}
                />
                <Route
                  path="/case-study/sharepoint-consulting-fl-usa-flohut-1"
                  element={<SharepointConsultingFlohut1 />}
                />
                <Route
                  path="/case-study/sharepoint-consulting-fl-usa-pace-1"
                  element={<SharepointConsultingPace1 />}
                />
                <Route
                  path="/case-study/application-development-ca-usa-pinscriptive-1"
                  element={<ApplicatioDevelopmentPinscriptive1 />}
                />
                <Route
                  path="/case-study/sharepoint-consulting-ct-usa-advanced-radiology-1"
                  element={<SharepointConsultingAdvancedRadiology1 />}
                />
                <Route
                  path="/case-study/machine-learning-ai-react-france-metriq-1"
                  element={<MachineLearningMetriq1 />}
                />
                <Route
                  path="/case-study/sharepoint-consulting-massachusetts-usa-cdm-smith-1"
                  element={<SharepointConsultingCDMSmith1 />}
                />
                <Route
                  path="/case-study/mobile-app-singapore-fibronostics-1"
                  element={<MobileAppFibronostics1 />}
                />
                <Route
                  path="/case-study/sharepoint-consulting-wa-usa-microsoft-2"
                  element={<SharepointConsultingMicrosoft2 />}
                />
                <Route
                  path="/case-study/sharepoint-consulting-usa-microsoft-3"
                  element={<SharepointConsultingMicrosoft3 />}
                />
                <Route
                  path="/case-study/sharepoint-ai-agent-usa-six-1"
                  element={<MicrosoftCopilotAgents />}
                />
                <Route
                  path="/case-study/sharepoint-norway-norconsult-1"
                  element={<SharepointConsultingNorwayNorconsult />}
                />
                <Route
                  path="/case-study/power-app-norway-norconsult-1"
                  element={<PowerAppNorwayNorconsult />}
                />
                <Route
                  path="/case-study/web-app-usa-notalvision-1"
                  element={<NotalScanlyHomeOct />}
                />
                <Route
                  path="/case-study/power-automate-norway-polygon-1"
                  element={<PolygonPowerapps />}
                />
                <Route
                  path="/case-study/ai-mindmap-generator-us-csod-1"
                  element={<CSODMindMap />}
                />
                <Route
                  path="/case-study/generative-ai-mail-assistant-us-csod-4"
                  element={<CSODMailAssistant />}
                />
                <Route
                  path="/case-study/ai-ppt-assistant-us-csod-2"
                  element={<CSODPPTAssistant />}
                />
                <Route
                  path="/case-study/ai-query-tool-us-csod-3"
                  element={<CSODQueryTool />}
                />
                <Route
                  path="/case-study/ai-writing-intelligence-platform-us-csod-6"
                  element={<CSODGrammarAssistant />}
                />
                <Route
                  path="/case-study/ai-content-creation-automation-us-csod-7"
                  element={<CSODContenAsistant />}
                />
                <Route
                  path="/case-study/ai-spreadsheet-insights-platform-us-csod-8"
                  element={<CSODSheetInsightsAI />}
                />
                <Route
                  path="/case-study/ai-quote-automation-platform-us-csod-9"
                  element={<CSODQuoteFlowAI />}
                />
                <Route
                  path="/case-study/ai-knowledge-casting-platform-us-csod-5"
                  element={<CSODCornerCast />}
                />
                <Route
                  path="/case-study/payment-integration-automation-uk-accurise-1"
                  element={<IntelligentPayment />}
                />
                <Route
                  path="/case-study/intelligent-invoice-processing-automation-uk-accurise-2"
                  element={<IntelligentInvoice />}
                />
                <Route
                  path="/case-study/generative-ai-lifestyle-image-platform-us-komar-1"
                  element={<GenerativeAI />}
                />
                <Route
                  path="/case-study/enterprise-cloud-cost-optimization-aws-1"
                  element={<EnterpriseCloudCostOptimizationAWS />}
                />
                <Route
                  path="/case-study/enterprise-edi-integration-warehouse-automation-us-komar-2"
                  element={<EDIIntegration />}
                />
                <Route
                  path="/case-study/employee-onboarding-automation-india-binary-republik-1"
                  element={<EmployeeOnboardingAutomation />}
                />
                <Route
                  path="/business-solutions/automate-invoice-processing-Xledger"
                  element={<AutomateInvoiceProcessing />}
                />
                <Route
                  path="/business-solutions/automate-payment-processing-Xledger"
                  element={<AutomatePaymentProcessing />}
                />
                <Route
                  path="/business-solutions/enhance-salesforce-cpq-experience"
                  element={<UnlockingSalesforceCPQ />}
                />
              </Routes>
            </Suspense>
          </main>
        </div>
      </ThemeProvider>
      {!location.pathname.includes("/careers") && (
        <Suspense fallback={null}>
          <ChatWidget />
        </Suspense>
      )}
    </div>
  );
};

export default App;
